/*
 * This unpublished material is proprietary to Vaticle.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of Vaticle.
 */

import { Component, DestroyRef, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, RouterLink } from "@angular/router";
import { ButtonComponent, PropertiesTableComponent, PropertiesTableRowComponent, SpinnerComponent } from "typedb-cloud-framework";
import { Cluster } from "../../../concept/cluster";
import { ApplicationState } from "../../../service/application-state.service";
import { ClusterApi } from "../../../service/cluster/cluster-api.service";
import { SpaceApi } from "../../../service/space/space-api.service";
import { ResourceService } from "../../../service/resource.service";
import { PageScaffoldComponent, ResourceAvailability } from "../../scaffold/page/page-scaffold.component";
import {
    BehaviorSubject, Observable, Subject, catchError, combineLatest, distinctUntilChanged,
    filter,
    first, interval, map, of, shareReplay, startWith, switchMap, tap
} from "rxjs";
import { AsyncPipe } from "@angular/common";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from "@angular/material/tooltip";
import { ServerLogsViewingPaneComponent } from "../server/logs-viewing-pane/server-logs-viewing-pane.component";
import { ClusterLogsViewingPaneComponent } from "./viewing-pane/cluster-logs-viewing-pane.component";
import { LogsControlBarComponent } from "./logs-control-bar/logs-control-bar.component";
import {
    DownloadClusterLogsData,
    DownloadClusterLogsDialogComponent
} from "./download-logs-dialog/download-cluster-logs-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import {
    DownloadClusterServerLogsData,
    DownloadClusterServerLogsDialogComponent
} from "../server/download-logs-dialog/download-cluster-server-logs-dialog.component";
import { SnackbarService } from "../../../service/snackbar.service";

@Component({
    selector: "tp-cluster-logs-page",
    templateUrl: "./cluster-logs-page.component.html",
    styleUrls: ["./cluster-logs-page.component.scss"],
    imports: [
        PageScaffoldComponent, AsyncPipe,
        MatTooltipModule, ServerLogsViewingPaneComponent, MatTabsModule, ClusterLogsViewingPaneComponent, LogsControlBarComponent,
        RouterLink,
    ]
})
export class ClusterLogsPageComponent implements OnInit, OnDestroy {
    @ViewChild("logViewer") logViewerEl?: ElementRef<HTMLPreElement>;
    readonly cluster$ = new BehaviorSubject<Cluster | null>(null);
    readonly serverIds$ = this.cluster$.pipe(
        filter(cluster => !!cluster),
        map(cluster => cluster!.serverCount),
        distinctUntilChanged(),
        map(serverCount => [...Array(serverCount).keys()])
    );
    readonly servers$ = this.cluster$.pipe(
        takeUntilDestroyed(),
        filter(cluster => !!cluster),
        map(cluster => cluster!),
        switchMap((cluster) => interval(5000).pipe(
            takeUntilDestroyed(this.destroyRef),
            startWith(-1),
            switchMap(() => this.clusterApi.listServers(cluster.uuid)),
        )),
        shareReplay(1),
    );
    readonly hasMultipleServers$ = this.cluster$.pipe(
        filter(cluster => !!cluster),
        map(cluster => cluster!.serverCount > 1),
        startWith(false)
    )
    private readonly unsub$ = new Subject<void>();
    readonly availability$: Observable<ResourceAvailability> = combineLatest([this.cluster$, this.servers$]).pipe(
        map(([x, y]) => {
            if (x?.status === "paused") return "failed"
            else return x && y ? "ready" : "loading"
        }), catchError(() => of("failed" as const))
    );
    isStreamingEnabled = true;
    selectedTabIndex?: number;

    constructor(
        private route: ActivatedRoute, private app: ApplicationState, private destroyRef: DestroyRef,
        private clusterApi: ClusterApi, private spaceApi: SpaceApi, private resourceService: ResourceService,
        private snackbar: SnackbarService, private dialog: MatDialog,
    ) {
        this.availability$.subscribe((availability) => {
            if (availability === "failed") this.snackbar.errorPersistent("Could not retrieve logs. Please make sure your cluster is running.")
        });
    }

    ngOnInit() {
        this.initDataLoader();
    }

    ngOnDestroy() {
        this.unsub$.next();
    }

    private initDataLoader() {
        this.route.params.pipe(
            filter((params) => params["space-id"] && params["cluster-id"]),
            map((params) => ({ spaceId: params["space-id"] as string, clusterId: params["cluster-id"] as string })),
            distinctUntilChanged((a, b) => a.clusterId === b.clusterId && a.spaceId === b.spaceId),
            tap(() => { this.unsub$.next(); }),
            switchMap(({ spaceId, clusterId }) => {
                return this.spaceApi.getSpace({ spaceId, teamUuid: this.app.requireCurrentTeam().uuid }, of(undefined)).pipe(
                    first(),
                    map((space) => ({ space: space.initial!, clusterId })),
                );
            }),
            switchMap(({ space, clusterId }) => {
                return this.clusterApi.getCluster({ clusterId: clusterId, spaceUuid: space.uuid }, this.unsub$);
            }),
        ).subscribe({
            next: (res) => {
                this.resourceService.processResponse({ resource$: this.cluster$, res: res, unsub$: this.unsub$, onResync: () => this.initDataLoader() });
            },
            error: (err) => {
                this.cluster$.error(err);
            }
        });
    }

    openDownloadLogsDialog() {
        if (this.selectedTabIndex) {
            this.servers$.pipe(first()).subscribe((servers) => {
                this.dialog.open<DownloadClusterServerLogsDialogComponent, DownloadClusterServerLogsData>(
                    DownloadClusterServerLogsDialogComponent,
                    { data: { cluster: this.cluster$.value!, server: servers[this.selectedTabIndex! - 1] } }
                )
            })
        } else {
            this.dialog.open<DownloadClusterLogsDialogComponent, DownloadClusterLogsData>(
                DownloadClusterLogsDialogComponent,
                { data: { cluster: this.cluster$.value! } }
            );
        }
    }
}
